import React, { useState, useRef, useEffect } from "react";
import { GoogleLogin } from "@react-oauth/google";
import { GoogleReCaptchaProvider, useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useDispatch } from "react-redux";
import { isValidPhoneNumber } from "libphonenumber-js/mobile";
import SimpleReactValidator from "simple-react-validator";
import DatePicker from "react-datepicker";
import API from "api.js";
import { persistor } from "store/index";
import {
  setToken,
  setUser,
  setLogo,
  setSub,
  setInstitute,
  setRegionAccess,
  setCoreSubscription,
  setLoader,
} from "action/index.js";
import PhoneInputField from "components/Inputs/PhoneInputField";
import { getSubdomain, isWhiteLabel } from "utils/whiteLabel";
import { toast } from "react-toastify";
import { isMobile } from "react-device-detect";

const isoToLocalDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};

const localDateToIso = (date) => {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * Scorecard unlock auth modal — uses the same dark modal shell as the rest of
 * the analyzer (modal-ov / modal-card). Validations mirror /auth/register.
 * Target score + exam date live here (not on the unlock gate).
 */
const ScorecardAuthModalInner = ({
  onClose,
  onAuthenticated,
  initialMode = "register",
  prefill = {},
}) => {
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [mode, setMode] = useState(initialMode); // register | otp | login
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [, forceUpdate] = useState(0);
  const busyRef = useRef(false);

  const [loginEmail, setLoginEmail] = useState(prefill.email || "");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginPwVisible, setLoginPwVisible] = useState(false);

  const [firstName, setFirstName] = useState(prefill.firstName || "");
  const [lastName, setLastName] = useState(prefill.lastName || "");
  const [email, setEmail] = useState(prefill.email || "");
  const [phone, setPhone] = useState(prefill.phone || "");
  const [password, setPassword] = useState("");
  const [cpassword, setCPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [target, setTarget] = useState(prefill.target || "");
  const [examDate, setExamDate] = useState(prefill.date || "");
  const [pwVisible, setPwVisible] = useState(false);
  const [cpwVisible, setCpwVisible] = useState(false);
  const [isDialCodeSelected, setIsDialCodeSelected] = useState(true);

  const validator = useRef(
    new SimpleReactValidator({
      messages: { alpha_space: "Allowed only letters and spaces." },
      validators: {
        firstnameMatch: {
          rule: (val) => val != null && val.toString().trim() !== "",
          required: true,
          message: "First Name is required",
        },
        lastnameMatch: {
          rule: (val) => val != null && val.toString().trim() !== "",
          required: true,
          message: "Last Name is required",
        },
        emailMatch: {
          rule: (val) => val != null && val.toString().trim() !== "",
          required: true,
          message: "Email is required",
        },
        emailValid: {
          rule: (val) => !!val && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val),
          message: "Please enter a valid email address",
        },
        phoneMatch: {
          rule: (val) => val != null && val.toString().trim() !== "",
          required: true,
          message: "Phone Number is required",
        },
        dialCodeRequired: {
          rule: () => false,
          message: "Please select a dial code",
        },
        phoneValid: {
          rule: (val) => !!val && isValidPhoneNumber(val),
          required: true,
          message: "Please enter a valid phone number for the selected country",
        },
        passMatch: {
          rule: (val) => val != null && val.toString().trim() !== "",
          required: true,
          message: "Password is required",
        },
        passwordStrong: {
          rule: (val) =>
            !!val && /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{6,}$/.test(val),
          message:
            "Password must be at least 6 characters and include 1 uppercase letter, 1 number, and 1 special character",
        },
        cpassMatch: {
          rule: (val) => val != null && val.toString().trim() !== "",
          required: true,
          message: "Confirm Password is required",
        },
        passwordMatch: {
          rule: (val) => val === password,
          required: true,
          message: "Passwords do not match.",
        },
        targetScore: {
          rule: (val) => {
            const n = parseInt(val, 10);
            return !Number.isNaN(n) && n >= 10 && n <= 90;
          },
          required: true,
          message: "Target score must be between 10 and 90",
        },
      },
    })
  );

  validator.current.rules.passwordMatch.rule = (val) => val === password;

  useEffect(() => {
    if (isDialCodeSelected === false) {
      validator.current.showMessageFor("Enter Phone Number");
      forceUpdate((n) => n + 1);
    }
  }, [isDialCodeSelected]);

  const applySession = (accessToken, user, image) => {
    dispatch(setToken(accessToken));
    dispatch(setUser(user));
    if (user?.institute !== undefined) dispatch(setInstitute(user.institute));
    if (user?.region_access !== undefined) dispatch(setRegionAccess(user.region_access));
    if (isWhiteLabel() && user?.core_subscription !== undefined) {
      dispatch(setCoreSubscription(user.core_subscription));
    }
    if (image) dispatch(setLogo(image));
    const sub = Array.isArray(user?.active_subscription)
      ? user.active_subscription.length > 0
      : Boolean(user?.active_subscription);
    dispatch(setSub(sub));
  };

  const unlockMeta = () => ({
    target: String(target).trim(),
    date: examDate.trim(),
  });

  const finishAuth = (user, via) => {
    if (typeof onAuthenticated === "function") {
      onAuthenticated(user || {}, { via, ...unlockMeta() });
    }
  };

  const runCaptcha = async (action) => {
    if (!executeRecaptcha) {
      throw new Error("Captcha not ready. Please try again in a moment.");
    }
    return executeRecaptcha(action);
  };

  const validateUnlockMeta = () => {
    validator.current.showMessageFor("Target Score");
    forceUpdate((n) => n + 1);
    const n = parseInt(target, 10);
    return !Number.isNaN(n) && n >= 10 && n <= 90;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (busyRef.current) return;
    setError("");
    if (!loginEmail.trim() || !loginPassword) {
      setError("Please enter your email and password.");
      return;
    }
    if (!validateUnlockMeta()) return;

    busyRef.current = true;
    setBusy(true);
    try {
      const recaptchaToken = await runCaptcha("login_action");
      const bodyFormData = new FormData();
      bodyFormData.set("email", loginEmail.trim());
      bodyFormData.set("password", loginPassword);
      bodyFormData.set("subdomain", isWhiteLabel() ? getSubdomain() || "" : " ");
      bodyFormData.set("recaptcha_token", recaptchaToken);

      const res = await API({
        url: `${import.meta.env.VITE_LOGIN_URL}/api/v2/login`,
        method: "POST",
        data: bodyFormData,
      });

      if (res?.status === 200 && res.data?.access_token) {
        applySession(res.data.access_token, res.data.user, res.data.image);
        finishAuth(res.data.user, "login");
        return;
      }
      setError("Login failed. Please check your details and try again.");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const buildRegisterForm = (recaptchaToken) => {
    const bodyFormData = new FormData();
    bodyFormData.set("email", email.trim());
    bodyFormData.set("password", password);
    bodyFormData.set("phone", phone);
    bodyFormData.set("first_name", firstName.trim());
    bodyFormData.set("last_name", lastName.trim());
    bodyFormData.set("user_name", "");
    bodyFormData.set("center", "");
    bodyFormData.set("score", "");
    bodyFormData.set("dob", "");
    bodyFormData.set("country_citizenship", "");
    bodyFormData.set("country_residence", "");
    bodyFormData.set("country_state", "");
    bodyFormData.set("recaptcha_token", recaptchaToken);
    return bodyFormData;
  };

  const handleSendOtp = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    if (busyRef.current) return;
    setError("");

    // When already in OTP mode this is a "Re-send" action — the registration
    // details were already validated to reach this step, so skip the full
    // register-form validation (it would otherwise flag the empty OTP field).
    const isResend = mode === "otp";

    if (!isResend) {
      if (isDialCodeSelected === false) {
        validator.current.showMessageFor("Enter Phone Number");
        forceUpdate((n) => n + 1);
        return;
      }
      if (!validator.current.allValid()) {
        validator.current.showMessages();
        forceUpdate((n) => n + 1);
        return;
      }
    }

    busyRef.current = true;
    setBusy(true);
    try {
      const recaptchaToken = await runCaptcha("otp_action");
      const res = await API({
        method: "POST",
        url: "/send/otp",
        data: buildRegisterForm(recaptchaToken),
      });
      if (res?.status === 200) {
        setMode("otp");
        toast.success("Please check your email for the verification code.");
      } else {
        setError("Could not send verification code. Please try again.");
      }
    } catch (err) {
      if (err?.response?.status === 422) {
        const se = err.response.data?.errors || {};
        setError(Object.values(se).flat()?.[0] || "Please fix the highlighted fields and try again.");
      } else {
        setError(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (busyRef.current) return;
    setError("");

    if (!otp || String(otp).trim().length !== 10) {
      validator.current.showMessageFor("otp");
      forceUpdate((n) => n + 1);
      return;
    }
    // Target/exam date were collected on the register step — still required
    if (!validateUnlockMeta()) {
      setError("Please go back and enter your target score and exam date.");
      return;
    }

    busyRef.current = true;
    setBusy(true);
    try {
      const recaptchaToken = await runCaptcha("signup_action");
      const bodyFormData = new FormData();
      bodyFormData.set("email", email.trim());
      bodyFormData.set("password", password);
      bodyFormData.set("phone", phone);
      bodyFormData.set("first_name", firstName.trim());
      bodyFormData.set("last_name", lastName.trim());
      bodyFormData.set("otp", String(otp).trim());
      bodyFormData.set("recaptcha_token", recaptchaToken);

      const res = await API({ method: "POST", url: "/signup", data: bodyFormData });

      if (res?.status === 200 && res.data?.data?.access_token) {
        applySession(res.data.data.access_token, res.data.data.user, res.data.data.image);
        try {
          await persistor.flush();
        } catch (flushErr) {
          console.warn("[ScorecardAuthModal] persistor flush failed", flushErr);
        }
        if (res.data?.message) toast.success(res.data.message);
        finishAuth(res.data.data.user, "signup");
        return;
      }
      setError(res?.data?.message || "Signup failed. Please try again.");
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || "Something went wrong. Please try again.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  // const handleGoogle = (credentialResponse) => {
  //   if (busyRef.current) return;
  //   if (!validateUnlockMeta()) return;

  //   busyRef.current = true;
  //   setBusy(true);
  //   setError("");
  //   dispatch(setLoader(false));

  //   API({ method: "POST", url: "/googleSignUp", data: credentialResponse })
  //     .then((res) => {
  //       if (res?.status === 200 && res.data?.original?.access_token) {
  //         applySession(
  //           res.data.original.access_token,
  //           res.data.original.user,
  //           res.data.original.image
  //         );
  //         finishAuth(res.data.original.user, "google");
  //       } else {
  //         setError("Google sign-in failed. Please try again.");
  //       }
  //     })
  //     .catch((err) => {
  //       setError(err?.response?.data?.message || "Google sign-in failed. Please try again.");
  //     })
  //     .finally(() => {
  //       busyRef.current = false;
  //       setBusy(false);
  //     });
  // };

  const title =
    mode === "login"
      ? "Log in to unlock your report"
      : mode === "otp"
      ? "Verify your email"
      : "Register to unlock your full report";

  const unlockFields = (
    <div className="sca-reg-row">
      <div className="sca-reg-field">
        <label>Target score*</label>
        <input
          type="number"
          min="10"
          max="90"
          placeholder="10–90"
          value={target}
          onChange={(e) => {
            setTarget(e.target.value);
            validator.current.showMessageFor("Target Score");
            forceUpdate((n) => n + 1);
          }}
          disabled={busy}
        />
        {validator.current.message("Target Score", target, "required|targetScore")}
      </div>
      <div className="sca-reg-field">
        <label>Exam date</label>
        <div className="sca-reg-date">
          <DatePicker
            selected={isoToLocalDate(examDate)}
            onChange={(date) => {
              setExamDate(localDateToIso(date));
              validator.current.showMessageFor("Exam Date");
              forceUpdate((n) => n + 1);
            }}
            dateFormat="dd/MM/yyyy"
            placeholderText="DD/MM/YYYY"
            minDate={new Date()}
            disabled={busy}
            popperClassName="sca-reg-datepicker-popper"
            portalId="sca-reg-datepicker-portal"
          />
        </div>
      </div>
    </div>
  );

  return (
    <div className="modal-ov show sca-reg-ov" role="dialog" aria-modal="true" aria-labelledby="sca-reg-title">
      <div className="modal-card sca-reg-card">
        <button type="button" className="sca-reg-close" onClick={onClose} disabled={busy} aria-label="Close">
          ×
        </button>
        <div className="modal-t" id="sca-reg-title">
          {title}
        </div>
        <div className="modal-b">
          {mode === "otp"
            ? `Enter the 10-digit code sent to ${email.trim()}. You'll confirm registration next, then we open your full report.`
            : "Your scores stay saved. After you finish, we unlock your full diagnosis on this page."}
        </div>

        <div className="sca-reg-scroll">
          {error && <div className="statusbar err sca-reg-err">{error}</div>}

          {mode === "login" && (
            <form className="sca-reg-form" onSubmit={handleLogin}>
              {unlockFields}
              <div className="sca-reg-field">
                <label>Email*</label>
                <input
                  type="email"
                  autoComplete="email"
                  placeholder="Email address"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  disabled={busy}
                />
              </div>
              <div className="sca-reg-field">
                <label>Password*</label>
                <div className="sca-reg-pw">
                  <input
                    type={loginPwVisible ? "text" : "password"}
                    autoComplete="current-password"
                    placeholder="Password"
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    disabled={busy}
                  />
                  <button type="button" className="sca-reg-eye" onClick={() => setLoginPwVisible((v) => !v)} tabIndex={-1}>
                    {loginPwVisible ? "Hide" : "Show"}
                  </button>
                </div>
              </div>
              <button type="submit" className="btn btn-primary sca-reg-submit" disabled={busy}>
                {busy ? "Signing in…" : "Log in & unlock report"}
              </button>
              {/* <div className="sca-reg-or">or continue with Google</div>
              <div className="sca-reg-google">
                <GoogleLogin
                  onSuccess={handleGoogle}
                  onError={() => setError("Google sign-in was cancelled or failed.")}
                  useOneTap={false}
                  theme="outline"
                  size="large"
                  width={isMobile ? "280" : "340"}
                />
              </div> */}
              <p className="sca-reg-switch">
                New here?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("register");
                    setError("");
                    validator.current.hideMessages();
                    forceUpdate((n) => n + 1);
                  }}
                  disabled={busy}
                >
                  Create an account
                </button>
              </p>
            </form>
          )}

          {mode === "register" && (
            <form className="sca-reg-form" onSubmit={handleSendOtp}>
              {unlockFields}
              <div className="sca-reg-row">
                <div className="sca-reg-field">
                  <label>First name*</label>
                  <input
                    placeholder="First name"
                    value={firstName}
                    autoComplete="off"
                    disabled={busy}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z ]*$/.test(value)) {
                        setFirstName(value);
                        validator.current.showMessageFor("First Name");
                        forceUpdate((n) => n + 1);
                      }
                    }}
                    onPaste={(e) => {
                      if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                    }}
                  />
                  {validator.current.message("First Name", firstName, "firstnameMatch")}
                </div>
                <div className="sca-reg-field">
                  <label>Last name*</label>
                  <input
                    placeholder="Last name"
                    value={lastName}
                    autoComplete="off"
                    disabled={busy}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (/^[A-Za-z ]*$/.test(value)) {
                        setLastName(value);
                        validator.current.showMessageFor("Last Name");
                        forceUpdate((n) => n + 1);
                      }
                    }}
                    onPaste={(e) => {
                      if (/[^A-Za-z ]/.test(e.clipboardData.getData("text"))) e.preventDefault();
                    }}
                  />
                  {validator.current.message("Last Name", lastName, "lastnameMatch")}
                </div>
              </div>

              <div className="sca-reg-field">
                <label>Email*</label>
                <input
                  type="email"
                  placeholder="Email address"
                  value={email}
                  autoComplete="email"
                  disabled={busy}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    validator.current.showMessageFor("Email");
                    forceUpdate((n) => n + 1);
                  }}
                />
                {validator.current.message("Email", email, "emailMatch|emailValid")}
              </div>

              <div className="sca-reg-field">
                <label>Phone*</label>
                <div className="sca-reg-phone">
                  <PhoneInputField
                    value={phone}
                    changeEvent={(val) => {
                      setPhone(val);
                      validator.current.showMessageFor("Enter Phone Number");
                      forceUpdate((n) => n + 1);
                    }}
                    onDialCodeStatusChange={setIsDialCodeSelected}
                    disabled={busy}
                  />
                </div>
                {isDialCodeSelected === false
                  ? validator.current.message("Enter Phone Number", phone, "dialCodeRequired")
                  : !phone
                  ? validator.current.message("Enter Phone Number", phone, "phoneMatch")
                  : validator.current.message("Enter Phone Number", phone, "phoneValid")}
              </div>

              <div className="sca-reg-field">
                <label>Password*</label>
                <div className="sca-reg-pw">
                  <input
                    type={pwVisible ? "text" : "password"}
                    placeholder="Password"
                    value={password}
                    autoComplete="new-password"
                    disabled={busy}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      validator.current.showMessageFor("Password");
                      forceUpdate((n) => n + 1);
                    }}
                  />
                  <button type="button" className="sca-reg-eye" onClick={() => setPwVisible((v) => !v)} tabIndex={-1}>
                    {pwVisible ? "Hide" : "Show"}
                  </button>
                </div>
                {password
                  ? validator.current.message("Password", password, "passwordStrong")
                  : validator.current.message("Password", password, "passMatch|passwordStrong")}
              </div>

              <div className="sca-reg-field">
                <label>Confirm password*</label>
                <div className="sca-reg-pw">
                  <input
                    type={cpwVisible ? "text" : "password"}
                    placeholder="Confirm password"
                    value={cpassword}
                    autoComplete="new-password"
                    disabled={busy}
                    onChange={(e) => {
                      setCPassword(e.target.value);
                      validator.current.showMessageFor("Confirm Password");
                      forceUpdate((n) => n + 1);
                    }}
                  />
                  <button type="button" className="sca-reg-eye" onClick={() => setCpwVisible((v) => !v)} tabIndex={-1}>
                    {cpwVisible ? "Hide" : "Show"}
                  </button>
                </div>
                {validator.current.message("Confirm Password", cpassword, "cpassMatch|passwordMatch")}
              </div>

              <button type="submit" className="btn btn-primary sca-reg-submit" disabled={busy}>
                {busy ? "Sending code…" : "Send verification code"}
              </button>
              <p className="sca-reg-switch">
                Already have an account?{" "}
                <button
                  type="button"
                  onClick={() => {
                    setMode("login");
                    setError("");
                    validator.current.hideMessages();
                    forceUpdate((n) => n + 1);
                  }}
                  disabled={busy}
                >
                  Log in
                </button>
              </p>
            </form>
          )}

          {mode === "otp" && (
            <form className="sca-reg-form" onSubmit={handleSignup}>
              <div className="sca-reg-field">
                <label>Verification code*</label>
                <input
                  type="text"
                  placeholder="Enter verification code"
                  autoComplete="one-time-code"
                  value={otp}
                  maxLength={10}
                  disabled={busy}
                  onChange={(e) => {
                    setOtp(e.target.value);
                    validator.current.showMessageFor("otp");
                    forceUpdate((n) => n + 1);
                  }}
                />
                {validator.current.message("otp", otp, "required|size:10")}
                <button type="button" className="sca-reg-resend" onClick={handleSendOtp} disabled={busy}>
                  Re-send verification code
                </button>
              </div>
              <button
                type="submit"
                className="btn btn-primary sca-reg-submit"
                disabled={busy || String(otp).length !== 10}
              >
                {busy ? "Creating account…" : "Verify & continue"}
              </button>
              <p className="sca-reg-switch">
                <button
                  type="button"
                  onClick={() => {
                    setMode("register");
                    setError("");
                    // Clear the stale "otp" field from the validator. Rendering
                    // the OTP step registered otp as invalid (empty); if left in
                    // place, allValid() on the register step keeps failing and
                    // "Send verification code" silently does nothing.
                    validator.current.purgeFields();
                    validator.current.hideMessages();
                    forceUpdate((n) => n + 1);
                  }}
                  disabled={busy}
                >
                  Edit details
                </button>
              </p>
            </form>
          )}
        </div>
      </div>
      <div id="sca-reg-datepicker-portal" />
    </div>
  );
};

const ScorecardAuthModal = (props) => {
  const siteKey = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
  if (!siteKey) {
    return (
      <div className="modal-ov show" role="dialog" aria-modal="true">
        <div className="modal-card">
          <div className="modal-t">Sign-up unavailable</div>
          <div className="modal-b">Captcha is not configured. Please try again later.</div>
          <div className="modal-acts">
            <button type="button" className="btn btn-primary" onClick={props.onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <GoogleReCaptchaProvider reCaptchaKey={siteKey}>
      <ScorecardAuthModalInner {...props} />
    </GoogleReCaptchaProvider>
  );
};

export default ScorecardAuthModal;
